import { useEffect, useState, FC, FormEvent, lazy } from "react";
import { useTranslation } from "react-i18next";
import { Radio, RadioChangeEvent, Space } from "antd";
import { CheckboxOptionType } from "antd/es/checkbox/Group";

import { Button, TextPlain, TitleH2 } from "components/generic";
import mainImg from "assets/images/Buitenlandse student aan het koken.webp";
import TextArea, { useTextArea } from "components/TextArea";
import {
  ContactGrid,
  ContactImg,
  ContactP,
  ContactTitle,
  FormSend
} from "./styles";
import { HeaderSEO } from "components/headerSEO";
import { ContentDiv, DialogWrapper } from "components/containers";
import { FAQ_ROUTE } from "utils/constants";
import { SmallLoader } from "../../../components/SmallLoader";
import { IEmail, ISeo } from "interfaces/interfaces";

const ModalLazy = lazy(() => import("../../../components/Modal"));

type TCategories = "QAfind" | "QArent" | "Collaboration" | "Media";

const Contact: FC = () => {
  const { t, i18n } = useTranslation("contact");
  const [required, setRequired] = useState(false);
  const [showModal, setShowModal] = useState<number>(0);
  const [categoryValue, setCategoryValue] = useState<TCategories>("QAfind");

  const categoryOptions: Array<CheckboxOptionType<TCategories>> = [
    {
      value: "QAfind",
      label: t("contact.form.category.qafind")
    },
    {
      value: "QArent",
      label: t("contact.form.category.qarent")
    },
    {
      value: "Collaboration",
      label: t("contact.form.category.collaboration")
    },
    {
      value: "Media",
      label: t("contact.form.category.media")
    }
  ];

  const question = {
    ...useTextArea("")
  };

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const sendData = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const notEmpty = [
      e.currentTarget.first_name.value,
      e.currentTarget.last_name.value,
      e.currentTarget.email.value,
      e.currentTarget.tel.value,
      e.currentTarget.question.value
    ].every((x) => x.length);

    if (!notEmpty) {
      setRequired(true);
    } else {
      setLoading(true);

      const requestOptions = {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: "contact_form",
          first_name: e.currentTarget.first_name.value as string,
          last_name: e.currentTarget.last_name.value as string,
          category: categoryValue as string,
          question: e.currentTarget.question.value as string,
          phone: e.currentTarget.tel.value as string,
          browser_language: i18n.language
        } as IEmail)
      };

      await fetch(
        process.env.REACT_APP_AWS_ENDPOINT! +
          `public/contact-us/${e.currentTarget.email.value as string}`,
        requestOptions
      )
        .then(() => {
          setCategoryValue("QAfind");
          question.onChange("");
          setShowModal(1);
          setRequired(false);
          const contactForm: HTMLFormElement | null = document.getElementById(
            "contactFormHospi"
          ) as HTMLFormElement;
          if (contactForm) contactForm.reset();
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const onCategoryChange = (e: RadioChangeEvent) => {
    setCategoryValue(e.target.value);
  };

  const seo = t("contact.seo", { returnObjects: true }) as ISeo;

  return (
    <ContentDiv>
      {seo && <HeaderSEO {...seo} />}
      <ContactTitle>
        {t("contact.title.1")}
        <span>{t("contact.title.2")}</span>
      </ContactTitle>
      <ContactGrid>
        <ContactImg src={mainImg} alt="" />
        <ContactP>
          {t("contact.text.1")}
          <br />
          <br />
          {t("contact.text.2")}
          <br />
          <br />
          {t("contact.text.3")}
          <a href={`/${i18n.language}${FAQ_ROUTE.slice(5)}`}> FAQ </a>
          {t("contact.text.4")}
        </ContactP>
      </ContactGrid>
      <FormSend
        id="contactFormHospi"
        role="form"
        onSubmit={async (e) => await sendData(e as FormEvent<HTMLFormElement>)}
      >
        <div id="formTitle">
          <h2>{t("contact.form.title")}</h2>
        </div>
        <div id="firstnameDiv">
          <p>{t("contact.form.firstName")}</p>
          <input
            pattern="\D{2,30}"
            title={t("contact.form.nameError")}
            name="first_name"
            required={required}
          />
        </div>
        <div id="lastnameDiv">
          <p>{t("contact.form.lastName")}</p>
          <input
            pattern="\D{2,30}"
            title={t("contact.form.nameError")}
            name="last_name"
            required={required}
          />
        </div>
        <div id="emailDiv">
          <p>{t("contact.form.email")}</p>
          <input
            title={t("contact.form.nameError")}
            type="email"
            name="email"
            placeholder="emailaddres@domein.com"
            required={required}
          />
        </div>
        <div id="phoneDiv">
          <p>{t("contact.form.phone")}</p>
          <input
            title={t("contact.form.nameError")}
            type="tel"
            name="tel"
            placeholder="+31 61231212"
            required={required}
          />
        </div>
        <div id="categoryDiv">
          <p>{t("contact.form.category.title")}</p>
          <Radio.Group
            onChange={onCategoryChange}
            value={categoryValue}
            // id="category"
          >
            <Space direction="vertical">
              {categoryOptions.map((o) => (
                <Radio key={o.value} value={o.value}>
                  {o.label}
                </Radio>
              ))}
            </Space>
          </Radio.Group>
        </div>
        <div id="questionDiv">
          <p>{t("contact.form.question")}</p>
          <TextArea
            {...question}
            id="questionText"
            name="question"
            required={required}
          />
        </div>
        <Button
          main
          type="submit"
          disabled={loading}
          style={{ minHeight: 38.8 }}
        >
          {loading ? (
            <SmallLoader height={16} borderWidth={2} color="white" />
          ) : (
            t("contact.form.butTetxt")
          )}
        </Button>
      </FormSend>
      {!!showModal && (
        <ModalLazy isShow={!!showModal} handleClose={() => setShowModal(0)}>
          <DialogWrapper>
            <TitleH2
              style={{
                textAlign: "left"
              }}
            >
              {t(`contact.email.title`)}
            </TitleH2>
            <TextPlain
              style={{
                whiteSpace: "pre-line",
                wordBreak: "break-word",
                textAlign: "left"
              }}
            >
              {t(`contact.email.text`)}
            </TextPlain>
            <div className="buttons">
              <span />
              <Button main onClick={() => setShowModal(0)}>
                {t(`contact.closeBox`)}
              </Button>
            </div>
          </DialogWrapper>
        </ModalLazy>
      )}
      <div style={{ height: 1, background: "white" }} />
    </ContentDiv>
  );
};

export default Contact;
